import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ordersApi, settingsApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { saveBlob } from '@/lib/download'
import {
  ErrorNote,
  Loading,
  PageHeader,
  SourceBadge,
  StatusBadge,
} from '@/components/common/Ui'
import CustomerPanel from '@/components/orders/CustomerPanel'
import TransportPanel from '@/components/orders/TransportPanel'
import DeliveryDatePanel from '@/components/orders/DeliveryDatePanel'
import ShipmentsPanel from '@/components/orders/ShipmentsPanel'
import SourcePanel from '@/components/orders/SourcePanel'
import OrderLineRow from '@/components/orders/OrderLineRow'
import Confirm from '@/components/common/Confirm'
import AddProduct from '@/components/orders/AddProduct'
import StatusTimeline from '@/components/orders/StatusTimeline'
import {
  dateTime,
  dayAfter,
  money,
  titleCase,
  DELIVERY_STATUS_LABELS,
  NEXT_STATUSES,
  ORDER_STATUS_LABELS,
} from '@/lib/format'
import { PERMS, can, useAuth } from '@/store/auth'

/**
 * The order's GST split by rate, e.g. [{ rate: '18%', amount: 69.71 }].
 *
 * A single row with no percent when nothing on the order carries a rate, so
 * an order of unpriced lines still shows its (zero) GST.
 */
function gstByRate(items) {
  const byRate = new Map()
  for (const item of items) {
    if (item.tax_rate == null) continue
    const rate = Number(item.tax_rate)
    byRate.set(rate, (byRate.get(rate) ?? 0) + Number(item.line_tax ?? 0))
  }
  if (!byRate.size) return [{ rate: '', amount: 0 }]
  return [...byRate.entries()]
    .sort(([a], [b]) => a - b)
    .map(([rate, amount]) => ({ rate: `${rate}%`, amount }))
}

export default function OrderDetail() {
  const { id } = useParams()
  const queryClient = useQueryClient()
  const [downloading, setDownloading] = useState(false)
  const [adding, setAdding] = useState(false)
  const { user } = useAuth()
  const [error, setError] = useState('')
  // Cancelling asks first: it cannot be undone, and it messages the customer.
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')
  // Correcting a wrong invoice number: admins only, with a reason.
  const [correcting, setCorrecting] = useState(false)
  const [correction, setCorrection] = useState({ invoiceId: null, number: '', reason: '' })
  const [correctionError, setCorrectionError] = useState('')
  const [invoiceMenu, setInvoiceMenu] = useState(false)
  const invoiceMenuRef = useRef(null)
  // A click anywhere else closes the menu.
  useEffect(() => {
    if (!invoiceMenu) return undefined
    const close = (event) => {
      if (!invoiceMenuRef.current?.contains(event.target)) setInvoiceMenu(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [invoiceMenu])

  const { data: order, isLoading, isError, refetch } = useQuery({
    queryKey: ['order', id],
    queryFn: () => ordersApi.get(id),
    // The customer changes this order from their own page — address,
    // transport, quantities — so coming back to this tab reloads it rather
    // than showing what it said when it was first opened.
    refetchOnWindowFocus: true,
  })

  // Whether confirming this order will message the customer. Read from the
  // server rather than assumed: an operator who believes nothing is sent, and
  // is wrong, quotes a customer a price by accident.
  const { data: runtime } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    staleTime: 5 * 60 * 1000,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['order', id] })
    queryClient.invalidateQueries({ queryKey: ['orders'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }

  const updateItem = useMutation({
    mutationFn: ({ itemId, payload }) => ordersApi.updateItem(id, itemId, payload),
    onSuccess: invalidate,
    onError: (e) => setError(errorMessage(e)),
  })

  const deleteItem = useMutation({
    mutationFn: (itemId) => ordersApi.deleteItem(id, itemId),
    onSuccess: invalidate,
    onError: (e) => setError(errorMessage(e)),
  })

  // Clears an acceptance below the minimum made before that option was
  // removed, so such a line can be held to its minimum again.
  const undoWaiveMinimum = useMutation({
    mutationFn: (itemId) => ordersApi.undoWaiveMinimum(id, itemId),
    onSuccess: invalidate,
    onError: (e) => setError(errorMessage(e)),
  })

  const addItem = useMutation({
    mutationFn: (payload) => ordersApi.addItem(id, payload),
    onSuccess: () => {
      setAdding(false)
      invalidate()
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const setStatus = useMutation({
    mutationFn: (payload) => ordersApi.setStatus(id, payload),
    onSuccess: (...args) => {
      setCancelling(false)
      setReason('')
      return invalidate(...args)
    },
    // Closed so the error shows on the page instead of behind the dialog.
    onError: (e) => {
      setCancelling(false)
      setError(errorMessage(e))
    },
  })

  const correctNumber = useMutation({
    mutationFn: () =>
      ordersApi.correctInvoiceNumber(correction.invoiceId, correction.number.trim(), correction.reason.trim()),
    onSuccess: () => {
      setCorrecting(false)
      setCorrection({ invoiceId: null, number: '', reason: '' })
      queryClient.invalidateQueries({ queryKey: ['order', id] })
    },
    // Kept open: the admin fixes the number or the reason and tries again.
    onError: (e) => setCorrectionError(errorMessage(e)),
  })

  if (isLoading) return <Loading />
  if (isError) return <ErrorNote message="Could not load this order" onRetry={refetch} />

  const open = !['delivered', 'cancelled'].includes(order.status)
  // Correcting lines and moving the order on are separate permissions, so a
  // role could later be allowed one without the other.
  const editable = can(user, PERMS.ordersWrite) && open
  const canMoveOn = can(user, PERMS.ordersStatus) && open
  // Only the lines that came out as a product. A line with nothing matched
  // carried no name, no rate and no total, and with the Heard column gone it
  // had nothing left to show but 'No product identified' — a row that says
  // only that it is empty.
  const items = order.items.filter((i) => i.product_id != null)

  // Whether our own reading of the order is still in question.
  //
  // Only while it is NEW. After that the customer has been through every line
  // on their own page, changed what was wrong and placed it — which answers
  // the matcher's doubts better than an operator squinting at the same guess
  // could. Order 0004 is the case: a line marked 'suggested' and noted 'no
  // quantity found', which the customer then gave a quantity of 50 and
  // confirmed. Flagging that is telling the shop to check something already
  // settled by the only person who could settle it.
  const reviewing = order.status === 'new'

  // Adding a product stops once the shop has accepted the order. From
  // CONFIRMED on it is being picked in the yard against a list somebody has
  // already printed, and a line appearing after that is one nobody packs.
  // Correcting and removing lines keep the wider rule; only adding is held
  // to these two.
  const canAddProduct = editable && ['new', 'pending'].includes(order.status)

  // Counted over the rows on screen, not over every row on the order.
  // Counting a line the operator cannot see leaves them looking at a tidy
  // list beside a dead Confirm button with nothing anywhere saying why.
  const unresolved = reviewing
    ? items.filter((i) => i.match_status === 'suggested').length
    : 0

  // Dispatch waits for the transporter's Reference No. — except a store
  // pickup, which the customer carries away themselves.
  const pickup = order.fulfilment === 'pickup'
  const needsReference = !order.transport_reference && !pickup
  const issuedInvoices = (order.invoices ?? []).filter((i) => i.status === 'issued')
  const canEditNumber = issuedInvoices.length > 0 && can(user, PERMS.invoicesCorrect)

  // The order's own invoice once it has one, else the order sheet.
  const downloadInvoice = async () => {
    setDownloading(true)
    try {
      const whole = wholeOrderInvoices.filter((i) => i.status === 'issued').at(-1)
      const { blob, filename } =
        whole && can(user, PERMS.invoicesRead)
          ? await ordersApi.invoicePdf(whole.id)
          : await ordersApi.pdf(id)
      saveBlob(blob, filename)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setDownloading(false)
    }
  }
  const shipmentInvoiceIds = new Set(
    (order.shipments ?? []).map((s) => s.invoice?.id).filter(Boolean),
  )
  const wholeOrderInvoices = (order.invoices ?? []).filter((i) => !shipmentInvoiceIds.has(i.id))
  const correcting_invoice = issuedInvoices.find((i) => i.id === correction.invoiceId)

  return (
    <div className="order-detail">
      <PageHeader
        title={order.order_number}
        subtitle={
          <>
            <Link to={`/customers?search=${order.customer_phone ?? ''}`}>{order.customer_name}</Link>
            {' · '}
            Tier {order.price_tier} · {dateTime(order.created_at)}
            {' · '}
            {/* Down here with the rest of the provenance. How the order
                arrived is a fact about it, not something anyone acts on. */}
            <SourceBadge source={order.source} />
          </>
        }
        actions={
          <div className="header-badges">
            <StatusBadge status={order.status} />
            {/* One button. Before the order is placed it gives the order
                sheet; once invoiced, a small menu: download the invoice, or
                — for those allowed — edit its number. */}
            <div className="invoice-menu" ref={invoiceMenuRef}>
              <button
                type="button"
                className="btn btn-sm"
                disabled={downloading}
                aria-haspopup={canEditNumber ? 'menu' : undefined}
                aria-expanded={canEditNumber ? invoiceMenu : undefined}
                onClick={() => (canEditNumber ? setInvoiceMenu((open) => !open) : downloadInvoice())}
              >
                {downloading ? 'Preparing…' : 'Invoice'}
                {canEditNumber && ' ▾'}
              </button>
              {invoiceMenu && (
                <div className="invoice-menu-list" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setInvoiceMenu(false)
                      downloadInvoice()
                    }}
                  >
                    Download
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setInvoiceMenu(false)
                      const latest = issuedInvoices[issuedInvoices.length - 1]
                      setCorrection({ invoiceId: latest.id, number: latest.number, reason: '' })
                      setCorrectionError('')
                      setCorrecting(true)
                    }}
                  >
                    Edit invoice number
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />

      {error && <ErrorNote message={error} />}

      <div className="detail-grid">
        <div className="main-column">
          <section className="lines-section">
            <div className="panel-title panel-title-row">
              <span>Order items</span>
              {canAddProduct && !adding && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    setError('')
                    setAdding(true)
                  }}
                >
                  + Add product
                </button>
              )}
            </div>
            {items.map((item) => (
              <OrderLineRow
                key={item.id}
                item={item}
                editable={editable}
                reviewing={reviewing}
                saving={updateItem.isPending}
                onSave={(payload) => {
                  setError('')
                  updateItem.mutate({ itemId: item.id, payload })
                }}
                onDelete={() => deleteItem.mutate(item.id)}
                onUndoWaiveMinimum={() => undoWaiveMinimum.mutate(item.id)}
              />
            ))}

            {adding && (
              <AddProduct
                tier={order.price_tier}
                saving={addItem.isPending}
                onCancel={() => setAdding(false)}
                onAdd={(payload) => {
                  setError('')
                  addItem.mutate(payload)
                }}
              />
            )}

            <div className="totals">
              <div>
                <span>Subtotal</span>
                <strong>{money(order.subtotal)}</strong>
              </div>
              {/* One row per rate, so each says its percent: an order of
                  18% wire and 5% nails has no single GST rate to print. */}
              {gstByRate(items).map(({ rate, amount }) => (
                <div key={rate}>
                  <span>GST {rate}</span>
                  <strong>{money(amount)}</strong>
                </div>
              ))}
              <div className="grand">
                <span>Total</span>
                <strong>{money(order.grand_total)}</strong>
              </div>
            </div>

            {order.notes && (
              <div className="order-notes">
                <div className="line-label">Customer note</div>
                <p>{order.notes}</p>
              </div>
            )}
          </section>

          {/* Under the items it counts: wide enough for the per-product table. */}
          <ShipmentsPanel
            order={order}
            canShip={can(user, PERMS.ordersStatus)}
            canReadInvoices={can(user, PERMS.invoicesRead)}
          />
        </div>

        <aside className="side-column">
          <CustomerPanel
            customer={order.customer}
            order={order}
            canChangeShipTo={
              can(user, PERMS.ordersWrite) &&
              !pickup &&
              ['new', 'pending', 'confirmed', 'packed'].includes(order.status)
            }
          />

          <DeliveryDatePanel order={order} editable={can(user, PERMS.ordersWrite)} />

          <TransportPanel
            order={order}
            editable={can(user, PERMS.deliveriesWrite)}
            needsReference={
              Boolean(NEXT_STATUSES[order.status]?.includes('dispatched')) &&
              !order.transport_reference &&
              !pickup
            }
          />

          {/* Delivery confirmation. Dispatched: the customer can confirm on
              their order link when it arrives (which also marks it
              Delivered), or staff mark it Delivered. Delivered: confirmed,
              and by whom. */}
          {(order.status === 'dispatched' || order.status === 'delivered') && (
            <div className="delivery-confirm-panel">
              <div className="panel-title">Delivery confirmation</div>
              {/* The customer said some items did not arrive. Open until the
                  delivery is confirmed; kept, marked resolved, after that. */}
              {order.delivery_issue_at && (
                <div
                  className={`delivery-issue${order.delivery_issue_open ? '' : ' resolved'}`}
                  role={order.delivery_issue_open ? 'alert' : undefined}
                >
                  <div className="delivery-issue-title">
                    {order.delivery_issue_open
                      ? '⚠ Delivery issue — items not delivered'
                      : 'Delivery issue reported (resolved)'}
                  </div>
                  <div className="delivery-issue-when">
                    Reported by the customer · {dateTime(order.delivery_issue_at)}
                  </div>
                  {order.delivery_issue_note && (
                    <div className="delivery-issue-note pre-line">{order.delivery_issue_note}</div>
                  )}
                </div>
              )}
              {order.delivery_confirmed_at ? (
                <dl className="kv">
                  <dt>Confirmed by</dt>
                  <dd className="confirm-done">
                    ✓ {order.delivery_confirmed_by === 'customer' ? 'Customer' : 'Admin'}
                  </dd>
                  <dt>Confirmed at</dt>
                  <dd>{dateTime(order.delivery_confirmed_at)}</dd>
                </dl>
              ) : order.status === 'dispatched' ? (
                <>
                  <p className="confirm-waiting">Waiting for customer confirmation</p>
                  <dl className="kv confirm-reminders">
                    <dt>Reminders sent</dt>
                    <dd>
                      {order.delivery_reminder_count || 0}
                      {order.delivery_reminder_count > 0 &&
                        ` · last ${dateTime(order.delivery_reminder_sent_at)}`}
                    </dd>
                    {dayAfter(order.delivery_reminder_sent_at) && (
                      <>
                        <dt>Next reminder</dt>
                        <dd>{dateTime(dayAfter(order.delivery_reminder_sent_at))}</dd>
                      </>
                    )}
                  </dl>
                  <p className="hint">
                    The customer is reminded on WhatsApp every 24 hours from dispatch, and a
                    new order from them is held, until the delivery is confirmed — by their
                    tapping <strong>All Items Are Delivered</strong> on their order link, or by
                    marking it Delivered here (confirmed as Admin).
                  </p>
                </>
              ) : (
                <p className="muted">Delivered before confirmations were recorded.</p>
              )}
            </div>
          )}

          <SourcePanel message={order.source_message} />

          {canMoveOn && (
            <div className="actions-panel">
              <div className="panel-title">Move this order on</div>
              {NEXT_STATUSES[order.status]?.length ? (
                NEXT_STATUSES[order.status].map((next) => (
                  <button
                    key={next}
                    type="button"
                    className={`btn btn-block ${next === 'cancelled' ? 'btn-danger' : 'btn-primary'}`}
                    disabled={
                      setStatus.isPending ||
                      (next === 'confirmed' && unresolved > 0) ||
                      (next === 'dispatched' && needsReference)
                    }
                    title={
                      next === 'dispatched' && needsReference
                        ? 'Add the Reference No. under Transport first'
                        : undefined
                    }
                    onClick={() => {
                      setError('')
                      if (next === 'cancelled') {
                        setCancelling(true)
                        return
                      }
                      // Ask for the send and let NOTIFY_CUSTOMERS decide: it
                      // is the real guard, and it is the one an operator can
                      // see the state of (the hint below reads the same
                      // value). Passing false here instead would mean the
                      // switch could be on, the screen could say so, and
                      // still nothing would be sent.
                      setStatus.mutate({ status: next, notify_customer: true })
                    }}
                  >
                    {ORDER_STATUS_LABELS[next]}
                  </button>
                ))
              ) : (
                <p className="muted">This order is closed.</p>
              )}
              {/* The server refuses it too; this says why before the click. */}
              {NEXT_STATUSES[order.status]?.includes('dispatched') && needsReference && (
                <p className="hint caution">
                  Add the transport Reference No. above before dispatching — it is sent to the
                  customer in the dispatch message.
                </p>
              )}
              {runtime && (
                <p className="hint">
                  {runtime.notify_customers
                    ? 'Moving this order on sends the customer a WhatsApp message.'
                    : 'Customer notifications are off — nothing is sent on WhatsApp.'}
                </p>
              )}
            </div>
          )}

          {order.delivery && (
            <div className="delivery-panel">
              <div className="panel-title">Delivery</div>
              <dl className="kv">
                <dt>Status</dt>
                <dd>
                  {DELIVERY_STATUS_LABELS[order.delivery.status] ??
                    titleCase(order.delivery.status)}
                </dd>
                {order.delivery.vehicle_number && (
                  <>
                    <dt>Vehicle</dt>
                    <dd>{order.delivery.vehicle_number}</dd>
                  </>
                )}
                {order.delivery.driver_name && (
                  <>
                    <dt>Driver</dt>
                    <dd>
                      {order.delivery.driver_name} · {order.delivery.driver_phone}
                    </dd>
                  </>
                )}
                {order.delivery.lr_number && (
                  <>
                    <dt>LR No.</dt>
                    <dd>{order.delivery.lr_number}</dd>
                  </>
                )}
              </dl>
            </div>
          )}

          <StatusTimeline order={order} />
        </aside>
      </div>
      {correcting_invoice && (
        <Confirm
          open={correcting}
          title={`Edit Invoice Number ${correcting_invoice.number}?`}
          message="The old and new number, your name and the time are recorded, and the invoice PDF carries the new number from now on."
          confirmLabel="Save"
          cancelLabel="Keep number"
          tone="primary"
          busy={correctNumber.isPending}
          onCancel={() => setCorrecting(false)}
          onConfirm={() => {
            setCorrectionError('')
            correctNumber.mutate()
          }}
        >
          {/* An order shipped in parts has an invoice per shipment. */}
          {issuedInvoices.length > 1 && (
            <label className="field">
              <span>Invoice</span>
              <select
                value={correction.invoiceId ?? ''}
                onChange={(e) => {
                  const chosen = issuedInvoices.find((i) => i.id === Number(e.target.value))
                  setCorrection((c) => ({ ...c, invoiceId: chosen.id, number: chosen.number }))
                }}
              >
                {issuedInvoices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.number}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="field">
            <span>New invoice number</span>
            <input
              value={correction.number}
              maxLength={32}
              onChange={(e) => setCorrection((c) => ({ ...c, number: e.target.value.toUpperCase() }))}
            />
          </label>
          {correctionError && <p className="field-error">{correctionError}</p>}
        </Confirm>
      )}
      <Confirm
        open={cancelling}
        title={`Cancel order ${order.order_number}?`}
        message={
          runtime?.notify_customers
            ? 'This cannot be undone. The customer is sent a WhatsApp message saying it is cancelled.'
            : 'This cannot be undone.'
        }
        confirmLabel="Cancel order"
        cancelLabel="Keep order"
        busy={setStatus.isPending}
        onCancel={() => {
          setCancelling(false)
          setReason('')
        }}
        onConfirm={() =>
          setStatus.mutate({
            status: 'cancelled',
            detail: reason.trim(),
            notify_customer: true,
          })
        }
      >
        <label className="field">
          <span>Reason (optional)</span>
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      </Confirm>
    </div>
  )
}
